import { ROOT_THREE } from "../../../util/constants.js";
export class Lorentzian {
    kind = 'lorentzian';
    /**
     * Full width at half maximum.
     * @default 500
     */
    fwhm;
    constructor(options = {}) {
        const { fwhm = 500 } = options;
        this.fwhm = fwhm;
    }
    fwhmToWidth(fwhm = this.fwhm) {
        return lorentzianFwhmToWidth(fwhm);
    }
    widthToFWHM(width) {
        return lorentzianWidthToFWHM(width);
    }
    fct(x) {
        return lorentzianFct(x, this.fwhm);
    }
    getArea(height = 1) {
        return getLorentzianArea({ fwhm: this.fwhm, height });
    }
    getFactor(area) {
        return getLorentzianFactor(area);
    }
    getData(options = {}) {
        return getLorentzianData(this, options);
    }
    calculateHeight(area = 1) {
        return calculateLorentzianHeight({ fwhm: this.fwhm, area });
    }
    getParameters() {
        return ['fwhm'];
    }
    /**
     * Descriptor of this shape, so `JSON.stringify` round-trips through `getShape1D`.
     * @returns the shape descriptor.
     */
    toJSON() {
        return { kind: this.kind, fwhm: this.fwhm };
    }
    derivative(x) {
        const { fct, dx, dFwhm } = lorentzianDerivative(x, this.fwhm);
        return { fct, dx, parameters: [dFwhm] };
    }
}
export const calculateLorentzianHeight = ({ fwhm = 1, area = 1 }) => {
    return (2 * area) / Math.PI / fwhm;
};
export const getLorentzianArea = (options) => {
    const { fwhm = 500, height = 1 } = options;
    return (height * Math.PI * fwhm) / 2;
};
export const lorentzianFct = (x, fwhm) => {
    return fwhm ** 2 / (4 * x ** 2 + fwhm ** 2);
};
/**
 * Analytical value and partial derivatives of the lorentzian function centered at x=0.
 * @param x - position at which to evaluate.
 * @param fwhm - full width at half maximum.
 * @returns the value `fct` and its partial derivatives with respect to `x` (`dx`) and `fwhm` (`dFwhm`).
 */
export function lorentzianDerivative(x, fwhm) {
    const denominator = 4 * x * x + fwhm * fwhm;
    const fct = (fwhm * fwhm) / denominator;
    const dx = (-8 * x * fwhm * fwhm) / (denominator * denominator);
    const dFwhm = (8 * fwhm * x * x) / (denominator * denominator);
    return { fct, dx, dFwhm };
}
export const lorentzianWidthToFWHM = (width) => {
    return width * ROOT_THREE;
};
export const lorentzianFwhmToWidth = (fwhm) => {
    return fwhm / ROOT_THREE;
};
const lorentzianQuantile = (p) => Math.tan(Math.PI * (p - 0.5));
export const getLorentzianFactor = (area = 0.9999) => {
    if (area >= 1) {
        throw new Error('area should be (0 - 1)');
    }
    const halfResidual = (1 - area) * 0.5;
    return ((lorentzianQuantile(1 - halfResidual) - lorentzianQuantile(halfResidual)) /
        2);
};
export const getLorentzianData = (shape = {}, options = {}) => {
    const { fwhm = 500 } = shape;
    const { factor = getLorentzianFactor(), height = calculateLorentzianHeight({ fwhm, area: 1 }), } = options;
    let { length } = options;
    if (!length) {
        length = Math.min(Math.ceil(fwhm * factor), 2 ** 25 - 1);
        if (length % 2 === 0)
            length++;
    }
    const center = (length - 1) / 2;
    const data = new Float64Array(length);
    for (let i = 0; i <= center; i++) {
        const value = lorentzianFct(i - center, fwhm) * height;
        data[i] = value;
        data[length - 1 - i] = value;
    }
    return data;
};
//# sourceMappingURL=Lorentzian.js.map